import axios from 'axios';
import { BacktestRun, HistoryData, HistoryMode, KiteCredentials, Mode, RebalanceSummary, TradingConfig, TradingStatus, User } from '../types';

// Dev: CRA on :3000 talks to Django on :8000. Prod build: same origin, REACT_APP_API_URL=/api (see .env.production).
const API_BASE_URL = (process.env.REACT_APP_API_URL as string) || 'http://localhost:8000/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
  withCredentials: true, // httpOnly JWT cookies
});

let refreshing: Promise<unknown> | null = null;

// On 401, refresh the access cookie once and retry. If that fails too, the AuthProvider will see no user.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const url: string = original?.url || '';
    const isAuthEndpoint = url.includes('/users/login') || url.includes('/users/token/refresh') || url.includes('/users/profile');
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post('/users/token/refresh/');
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (refreshError) {
        refreshing = null;
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ message: string; user: User }>('/users/login/', { email, password }).then((r) => r.data),
  logout: () => api.post('/users/logout/').then((r) => r.data),
  profile: () => api.get<User>('/users/profile/').then((r) => r.data),
};

export const kiteApi = {
  loginUrl: () => api.get<{ login_url: string }>('/brokers/kite/login-url/').then((r) => r.data),
  credentials: () => api.get<KiteCredentials>('/brokers/kite/credentials/').then((r) => r.data),
  saveCredentials: (api_key: string, api_secret: string) =>
    api.put<KiteCredentials>('/brokers/kite/credentials/', { api_key, api_secret }).then((r) => r.data),
};

export const tradingApi = {
  status: () => api.get<TradingStatus>('/trading/status/').then((r) => r.data),
  config: () => api.get<TradingConfig>('/trading/config/').then((r) => r.data),
  saveConfig: (cfg: TradingConfig) => api.put<TradingConfig>('/trading/config/', cfg).then((r) => r.data),
  setMode: (mode: Mode) =>
    api.post<{ mode: Mode }>('/trading/mode/', mode === 'LIVE' ? { mode, confirm: 'LIVE' } : { mode }).then((r) => r.data),
  rebalanceNow: () => api.post<RebalanceSummary>('/trading/rebalance/', {}, { timeout: 180000 }).then((r) => r.data),
  emergencyStop: () => api.post<{ closed: number }>('/trading/emergency-stop/', {}, { timeout: 120000 }).then((r) => r.data),
  resume: () => api.post<{ enabled: boolean }>('/trading/resume/').then((r) => r.data),
  history: (days: number, mode: HistoryMode) =>
    api.get<HistoryData>('/trading/history/', { params: { days, mode } }).then((r) => r.data),
  backtestStatus: () => api.get<{ run: BacktestRun | null }>('/trading/backtest/').then((r) => r.data),
  backtestStart: (date_from: string, date_to: string) =>
    api.post<{ run: BacktestRun }>('/trading/backtest/', { date_from, date_to }).then((r) => r.data),
};

/** Pull a readable message out of an axios error. */
export const errorMessage = (err: any, fallback = 'Something went wrong'): string => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  return Object.entries(data)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
    .join('. ') || fallback;
};
